/* eslint-disable max-lines */

import {values, map, compact, fromPairs, mapValues} from 'lodash';
import theme from '../../../theme';
import concatMapboxStyles from '../../mapbox/concatMapboxStyles';
import waypointIcons from '../../../icons/waypoints/icons';
import waypointPins from '../../../icons/waypoints/pins';
import opacifyMapboxStyle from '../../mapbox/opacifyMapboxStyle';
import {blurOpacity} from './constants';
import {waypointFixedLayerIds} from './fixedLayerIds';

interface GetWaypointsMapboxStyleOptions {
    highlight: MapHighlight;
    interaction: MapInteraction;
    isPrinting: boolean;
    searchResult: SearchResult | undefined;
    showWaypointLabels: undefined | boolean;
    showWaypoints: boolean;
    waypoints: {[id: string]: Waypoint};
    foldersMembership: MapFoldersMembership;
    user: User | undefined;
}

type LocalFixedLayerIds = typeof waypointFixedLayerIds['localUser'];
type RemoteFixedLayerIds = typeof waypointFixedLayerIds['remoteUser'];

export default function getStyle(options: GetWaypointsMapboxStyleOptions) {
    return {
        metadata: {
            uniqueTilecacheKey: 'waypoints'
        },
        ...concatMapboxStyles(compact([
            opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : blurOpacity) : 1,
                getRemoteUserStyle(waypointFixedLayerIds.remoteUser, options)
            ),
            opacifyMapboxStyle(
                options.highlight ? (options.isPrinting ? 0 : blurOpacity) : 1,
                getLocalUserStyle(waypointFixedLayerIds.localUser, options)
            ),
            getRemoteHighlightStyle(waypointFixedLayerIds.remoteHighlight, options),
            getLocalHighlightStyle(waypointFixedLayerIds.localHighlight, options),
            getSearchResultStyle(waypointFixedLayerIds.searchResult, options),
        ])),
        glyphs: 'https://static.gaiagps.com/GaiaTopoGL/glyphs/{fontstack}/{range}.pbf',
    };
}

function getSearchResultCoordinates(searchResult: SearchResult): [number, number] | undefined {
    if (searchResult.centroid) return searchResult.centroid;
    else if (
        searchResult.bounds &&
        searchResult.bounds[0] === searchResult.bounds[2] &&
        searchResult.bounds[1] === searchResult.bounds[3]
    ) return [searchResult.bounds[0], searchResult.bounds[1]];
}

function getSearchResultStyle(layerIds: typeof waypointFixedLayerIds['searchResult'], {
    searchResult,
    isPrinting,
    interaction
}: GetWaypointsMapboxStyleOptions): mapboxgl.Style | undefined {

    if (!searchResult) return undefined;

    const coordinates = getSearchResultCoordinates(searchResult);
    if (!coordinates) return undefined;
    if (searchResult.subtype === 'locality') return undefined;
    if (isPrinting) return undefined;

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'searchResult'
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: {
                        type: 'Point',
                        coordinates
                    },
                    properties: searchResult,
                }
            }
        },
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                paint: {
                    'icon-opacity': 0.5,
                },
                layout: {
                    'icon-image': 'searchResultIcon',
                }
            })
        ]
    };
}

function getLocalUserStyle(layerIds: LocalFixedLayerIds, {
    interaction,
    showWaypointLabels,
    showWaypoints,
    waypoints,
    highlight,
    foldersMembership,
}: GetWaypointsMapboxStyleOptions): mapboxgl.Style | undefined {
    if (!showWaypoints) return;

    const features = values(waypoints).filter((waypoint: Waypoint) => (
        isLocallyVisible(waypoint) && !isHighlighted(highlight, waypoint, foldersMembership)
    ));
    if (!features.length) return;

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'localUser'
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features
                },
            }
        },
        layers: [
            getCircleLayer({
                id: layerIds.circle,
                interaction
            }),
            getPinLayer({
                id: layerIds.pin,
                interaction,
                showWaypointLabels,
            }),
        ]
    };
}

interface GetCircleLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: MapInteraction;
    id: string;
}

function getCircleLayer({interaction, paint, ...layer}: GetCircleLayerOptions): mapboxgl.Layer {
    return {
        type: 'circle',
        source: 'source',
        filter: interaction.type === 'movingWaypoint' ?
            ['==', ['get', 'pointLocalId'], interaction.pointLocalId] :
            ['literal', false],
        paint: {
            'circle-stroke-color': theme.palette.primary.main,
            'circle-stroke-width': 2,
            'circle-opacity': 0.5,
            'circle-color': theme.palette.primary.main,
            'circle-radius': 60,
            'circle-translate': [0, -18],
            ...paint
        },
        ...layer
    };
}

function isLocallyVisible(waypoint: Waypoint) {
    return !waypoint.properties.isLocallyDeleted && waypoint.properties.attr !== 'route';
}

function isHighlighted(
    highlight: MapHighlight,
    waypoint: Waypoint,
    foldersMembership: MapFoldersMembership
) {
    if (!highlight) {
        return false;
    } else if (highlight.type === 'waypoint') {
        return highlight.id === waypoint.properties.pointLocalId;
    } else if (highlight.type === 'track') {
        return highlight.id === waypoint.properties.trackLocalId;
    } else if (highlight.type === 'folder') {
        const folder = foldersMembership[highlight.id];
        return folder && (
            folder.points[waypoint.properties.pointLocalId] ||
            (waypoint.properties.trackLocalId && folder.tracks[waypoint.properties.trackLocalId])
        );
    } else {
        return false;
    }
}

function getLocalHighlightStyle(layerIds: LocalFixedLayerIds, {
    interaction,
    showWaypointLabels,
    waypoints,
    highlight,
    foldersMembership,
}: GetWaypointsMapboxStyleOptions): mapboxgl.Style | undefined {

    const features = values(waypoints).filter((waypoint: Waypoint) => (
        isLocallyVisible(waypoint) && isHighlighted(highlight, waypoint, foldersMembership)
    ));
    if (!features.length) return;

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'localHighlight'
        },
        sources: {
            source: {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features,
                },
            }
        },
        layers: [
            getCircleLayer({id: layerIds.circle, interaction}),
            getPinLayer({id: layerIds.pin, interaction, showWaypointLabels}),
        ]
    };
}

function getRemoteHighlightStyle(layerIds: RemoteFixedLayerIds, {
    interaction,
    highlight,
    showWaypointLabels,
    waypoints,
}: GetWaypointsMapboxStyleOptions): mapboxgl.Style | undefined {

    const source = getRemoteHighlightSource(highlight);
    if (!source) return;

    const filter = getRemoteFilter(waypoints);

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'remoteHighlight'
        },
        sources: {source},
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                showWaypointLabels,
                ...source.type === 'vector' && {'source-layer': 'waypoints'},
                filter,
            }),
        ]
    };
}

function getRemoteHighlightSource(highlight: MapHighlight): mapboxgl.AnySourceData | undefined {
    if (highlight && highlight.type === 'waypoint') {
        return {
            type: 'geojson',
            // eslint-disable-next-line max-len
            data: `${window.location.origin}/api/objects/waypoint/${highlight.id}/?pubLink=${highlight.pubLink}`
        };

    } else if (highlight && highlight.type === 'folder') {
        return {
            type: 'vector',
            // eslint-disable-next-line max-len
            tiles: [`${window.location.origin}/api/tiles/folders/${highlight.id}/{z}/{x}/{y}.mvt?pubLink=${highlight.pubLink}`]
        };

    } else if (highlight && highlight.type === 'track') {
        return {
            type: 'geojson',
            // eslint-disable-next-line max-len
            data: `${window.location.origin}/api/objects/track/${highlight.id}/?pubLink=${highlight.pubLink}`
        };
    }
}

function getRemoteUserStyle(layerIds: RemoteFixedLayerIds, {
    interaction,
    showWaypoints,
    waypoints,
    showWaypointLabels,
    highlight,
    user,
}: GetWaypointsMapboxStyleOptions): mapboxgl.Style | undefined {

    if (!user || !user.isAuthenticated) return;
    if (!showWaypoints) return;

    const filter = [
        'all',
        getRemoteFilter(waypoints),
        highlight && highlight.type === 'waypoint' ?
            ['!=', ['get', 'id'], highlight.id] :
            ['literal', true]
    ];

    return {
        version: 8,
        metadata: {
            uniqueTilecacheKey: 'remoteUser'
        },
        sources: {
            source: {
                type: 'vector',
                tiles: [`${window.location.origin}/api/tiles/waypoints/{z}/{x}/{y}.mvt`],
                minzoom: 3,
                maxzoom: 14
            }
        },
        layers: [
            getPinLayer({
                id: layerIds.pin,
                interaction,
                'source-layer': 'waypoints',
                filter,
                showWaypointLabels,
            }),
        ]
    };
}

interface GetPinLayerOptions extends Partial<mapboxgl.Layer> {
    interaction: MapInteraction;
    showWaypointLabels?: boolean;
    id: string;
}

function getPinLayer({
    interaction,
    paint,
    layout,
    showWaypointLabels,
    ...layer
}: GetPinLayerOptions): mapboxgl.Layer {

    return {
        type: 'symbol',
        source: 'source',
        minzoom: 3,
        paint: {
            'text-color': '#000',
            'text-halo-color': '#fff',
            'text-halo-width': 1,
            'icon-opacity': getOpacityExpression(interaction),
            'text-opacity': getOpacityExpression(interaction),
            ...paint,
        },
        layout: {
            'icon-ignore-placement': true,
            'icon-allow-overlap': true,
            'icon-anchor': 'bottom',
            'icon-size': 1.25,
            'text-field': showWaypointLabels ? '{title}' : '',
            'text-ignore-placement': true,
            'text-allow-overlap': true,
            'text-size': 10,
            'text-anchor': 'top',
            'text-optional': true,
            'icon-image': getIconImageExpression(),
            'text-font': ['Regular'],
            ...layout,
        },
        ...layer,
    };
}

function getIconImageExpression(): mapboxgl.Expression {
    return [
        'case',
        ['!', ['has', 'icon']],
        'red-pin-down.png',
        [
            'has',
            ['get', 'icon'],
            ['literal', mapValues({...waypointIcons, ...waypointPins}, () => true)]
        ],
        ['get', 'icon'],
        'red-pin-down.png',
    ];
}

function getOpacityExpression(interaction: MapInteraction): mapboxgl.Expression | number {
    return interaction.type === 'movingWaypoint' ?
        ['case', ['==', ['get', 'pointLocalId'], interaction.pointLocalId], 1, blurOpacity] :
        interaction.type === 'editingRoute' ?
            ['case', ['==', ['get', 'trackLocalId'], interaction.trackLocalId], 1, blurOpacity] :
            1;
}

function getRemoteFilter(waypoints: Record<string, Waypoint>) {
    return [
        'all',
        ['has', ['geometry-type'], ['literal', {Point: true, MultiPoint: true}]],
        ['!', ['has', ['get', 'id'], ['literal', fromPairs(map(waypoints, waypoint =>
            [waypoint.properties.id, true]
        ))]]],
        ['!=', ['get', 'attr'], 'route'],
    ];
}
